import { onBeforeUnmount } from 'vue'
import type { Ref } from 'vue'

import type {
  MacroEditorAction,
  MacroEditorKeyAction,
  MacroEditorWheelAction,
  WheelType,
} from '../types'
import {
  getMouseHidCodeByButton,
  resolveKeyboardHidFromEvent,
  resolveWheelDirectionFromEvent,
} from '../utils/keycodeMaps'

interface MacroRecordingParams {
  reference: Ref<HTMLElement | undefined>
  isRecording: Ref<boolean>
  onRecord: (record: MacroEditorAction, reservedRecords?: MacroEditorAction[]) => boolean
  onCapturedForSingle: (record: MacroEditorKeyAction | MacroEditorWheelAction) => boolean
  /** 设备支持记录的滚轮方向，用于过滤不支持的滚轮事件 */
  enabledWheels: WheelType[]
}

/**
 * 统一监听键盘/鼠标/滚轮并标准化为编辑器动作。
 *
 * 会改变行为的组合键无法录制，比如 ctrl+w/alt+tab 等等
 */
export function useMacroRecordingEvents(params: MacroRecordingParams) {
  const { reference, isRecording, onRecord, onCapturedForSingle, enabledWheels } = params

  const pressedKeySet = new Set<string>()
  let lastActionAt = 0
  let attachedTarget: HTMLElement | null = null
  // onRecord 失败（例如达到字节上限）后立刻停收，不等待 isRecording 回写
  let halted = false

  // 键盘事件唯一标识
  const getKeyboardIdentity = (event: KeyboardEvent) => {
    if (event.code) return `key_${event.code}`

    const legacyKeyCode = event.keyCode || (event as KeyboardEvent & { which?: number }).which
    if (legacyKeyCode != null) return `key_${legacyKeyCode}`

    return `key_${event.key}`
  }

  /**
   * 判断鼠标事件是否应该忽略
   * 如果点击的元素或其祖先带有 data-recording-control="true" 属性，返回true
   * 用来排除录制控制按钮自身（比如停止录制按钮）被误录进去
   */
  const shouldIgnoreMouseEvent = (event: Event) => {
    const target = event.target as HTMLElement | null
    return !!target?.closest?.('[data-recording-control="true"]')
  }

  const haltIfRejected = (accepted: boolean) => {
    if (!accepted) halted = true
    return accepted
  }

  const shouldSkipRecordingEvent = (event: Event) => {
    if (!isRecording.value) return true
    if (!halted) return false
    event.preventDefault()
    event.stopPropagation()
    return true
  }

  /**
   * 计算距离上一次操作的时间间隔
   * 如果 > 0 就插入一个延迟操作
   */
  const handleRecordDelayIfNeeded = (nextRecord: MacroEditorAction) => {
    const now = Date.now()
    const interval = lastActionAt === 0 ? 0 : now - lastActionAt
    lastActionAt = now

    if (interval > 0) {
      return haltIfRejected(onRecord({ type: 4, time: interval }, [nextRecord]))
    }

    return true
  }

  const handleKeyboardDown = (event: KeyboardEvent) => {
    if (shouldSkipRecordingEvent(event)) return

    event.preventDefault()
    event.stopPropagation()

    const identity = getKeyboardIdentity(event)
    if (pressedKeySet.has(identity)) return

    const hidCode = resolveKeyboardHidFromEvent(event)
    if (hidCode == null) return

    pressedKeySet.add(identity)

    const payload: MacroEditorKeyAction = {
      type: 1,
      keystroke: hidCode,
      action: 1,
    }

    if (onCapturedForSingle(payload)) return

    if (!handleRecordDelayIfNeeded(payload)) {
      pressedKeySet.delete(identity)
      return
    }
    if (!haltIfRejected(onRecord(payload))) {
      pressedKeySet.delete(identity)
    }
  }

  const handleKeyboardUp = (event: KeyboardEvent) => {
    if (shouldSkipRecordingEvent(event)) return

    event.preventDefault()
    event.stopPropagation()

    const identity = getKeyboardIdentity(event)
    if (!pressedKeySet.has(identity)) return

    const hidCode = resolveKeyboardHidFromEvent(event)
    pressedKeySet.delete(identity)

    if (hidCode == null) return

    const payload: MacroEditorKeyAction = {
      type: 1,
      keystroke: hidCode,
      action: 0,
    }

    if (onCapturedForSingle(payload)) {
      return
    }

    if (!handleRecordDelayIfNeeded(payload)) return
    haltIfRejected(onRecord(payload))
  }

  const handleMouseDown = (event: MouseEvent) => {
    if (shouldSkipRecordingEvent(event) || shouldIgnoreMouseEvent(event)) return

    event.preventDefault()
    event.stopPropagation()

    // preventDefault 会阻止浏览器在 mousedown 时给元素赋予焦点，
    attachedTarget?.focus()

    const hidCode = getMouseHidCodeByButton(event.button)
    if (hidCode == null) return

    const identity = `mouse_${event.button}`
    if (pressedKeySet.has(identity)) return

    pressedKeySet.add(identity)

    const payload: MacroEditorKeyAction = {
      type: 1,
      keystroke: hidCode,
      action: 1,
    }

    if (onCapturedForSingle(payload)) return

    if (!handleRecordDelayIfNeeded(payload)) {
      pressedKeySet.delete(identity)
      return
    }
    if (!haltIfRejected(onRecord(payload))) {
      pressedKeySet.delete(identity)
    }
  }

  const handleMouseUp = (event: MouseEvent) => {
    if (shouldSkipRecordingEvent(event) || shouldIgnoreMouseEvent(event)) return

    event.preventDefault()
    event.stopPropagation()

    const hidCode = getMouseHidCodeByButton(event.button)
    if (hidCode == null) return

    const identity = `mouse_${event.button}`
    if (!pressedKeySet.has(identity)) return

    pressedKeySet.delete(identity)

    const payload: MacroEditorKeyAction = {
      type: 1,
      keystroke: hidCode,
      action: 0,
    }

    if (onCapturedForSingle(payload)) {
      return
    }

    if (!handleRecordDelayIfNeeded(payload)) return
    haltIfRejected(onRecord(payload))
  }

  const handleWheel = (event: WheelEvent) => {
    if (shouldSkipRecordingEvent(event)) return

    // 若设备不支持任何滚轮录制，直接跳过
    if (enabledWheels.length === 0) return

    // 根据原始 delta 判断滚轮主方向，仅主方向在 enabledWheels 中时才继续
    const absX = Math.abs(event.deltaX)
    const absY = Math.abs(event.deltaY)
    const isHorizontalScroll = absX > absY
    const isVerticalScroll = absY >= absX && absY > 0

    if (isHorizontalScroll && !enabledWheels.includes('horizontal')) return
    if (isVerticalScroll && !enabledWheels.includes('vertical')) return

    event.preventDefault()
    event.stopPropagation()

    const direction = resolveWheelDirectionFromEvent(event)
    const payload: MacroEditorWheelAction = {
      type: 3,
      ...direction,
    }

    if (onCapturedForSingle(payload)) {
      return
    }

    if (!handleRecordDelayIfNeeded(payload)) return
    haltIfRejected(onRecord(payload))
  }

  const handleContextMenu = (event: MouseEvent) => {
    if (!isRecording.value) return
    event.preventDefault()
    event.stopPropagation()
  }

  const handleAuxClick = (event: MouseEvent) => {
    if (shouldSkipRecordingEvent(event) || shouldIgnoreMouseEvent(event)) return

    const hidCode = getMouseHidCodeByButton(event.button)
    if (hidCode == null) return

    event.preventDefault()
    event.stopPropagation()
  }

  const bindListeners = (target: HTMLElement) => {
    target.addEventListener('mousedown', handleMouseDown, true)
    target.addEventListener('mouseup', handleMouseUp, true)
    target.addEventListener('wheel', handleWheel, { passive: false, capture: true })
    target.addEventListener('contextmenu', handleContextMenu, true)
    target.addEventListener('auxclick', handleAuxClick, true)
    target.addEventListener('keydown', handleKeyboardDown, true)
    target.addEventListener('keyup', handleKeyboardUp, true)
  }

  const unbindListeners = (target: HTMLElement) => {
    target.removeEventListener('mousedown', handleMouseDown, true)
    target.removeEventListener('mouseup', handleMouseUp, true)
    target.removeEventListener('wheel', handleWheel as EventListener, true)
    target.removeEventListener('contextmenu', handleContextMenu, true)
    target.removeEventListener('auxclick', handleAuxClick, true)
    target.removeEventListener('keydown', handleKeyboardDown, true)
    target.removeEventListener('keyup', handleKeyboardUp, true)
  }

  const attach = () => {
    const target = reference.value ?? null
    if (!target) return

    if (attachedTarget === target) return
    if (attachedTarget) {
      unbindListeners(attachedTarget)
    }

    bindListeners(target)
    attachedTarget = target
  }

  const detach = () => {
    if (!attachedTarget) return
    unbindListeners(attachedTarget)
    attachedTarget = null
  }

  const resetRecorderState = () => {
    pressedKeySet.clear()
    lastActionAt = 0
    halted = false
  }

  onBeforeUnmount(() => {
    detach()
    resetRecorderState()
  })

  return {
    attach,
    detach,
    resetRecorderState,
  }
}
